import moment from 'moment';
import styles from './ListingRow.module.css';
import Text from '../../atoms/typography/Text';
import { ColorEnum, FontSizeEnum, FontWeightEnum } from '../../enums';
import MoneyEuroCircleLineIcon from '../../atoms/icons/MoneyEuroCircleLineIcon';
import { classNames, formatPrice, formatRooms, formatSurface } from '../../utils';
import IconWithText from '../../atoms/typography/IconWithText';
import Ruler2LineIcon from '../../atoms/icons/Ruler2LineIcon';
import DoorOpenLineIcon from '../../atoms/icons/DoorOpenLineIcon';
import ButtonNew from '../../molecules/button/Button';
import UserSearchLineIcon from '../../atoms/icons/UserSearchLineIcon';
import ArchiveButton from '../archive-button/ArchiveButton';
import ArchiveListingModal from '../../../components/modals/archiveListingModal/ArchiveListingModal';
import { useNavigate } from 'react-router-dom';
import UnarchiveListingModal from '../../../components/modals/unarchiveListingModal/UnarchiveListingModal';
import { CheckboxInput } from '../../molecules/checkbox-input';
import { MouseEvent, useEffect, useState } from 'react';
import BulkActionDropdownNew from '../bulk-action-dropdown/BulkActionDropdownNew';
import { useRoutePath } from '../../utils/useRoutePath';
import { BulkActionDropdownCustomEventType } from '../bulk-action-dropdown/BulkActionEventManager';
import UserAddLineIcon from '../../atoms/icons/UserAddLineIcon';
import UserFollowLineIcon from '../../atoms/icons/UserFollowLineIcon';

export interface ListingDetails {
    agentId?: string;
    availableAt?: string;
    city?: string;
    closedAt?: string;
    createdAt?: string;
    enrichment?: string;
    houseNumber?: string;
    id?: string;
    numberOfLeadsToAssess?: number;
    numberOfSuitableLeads?: number;
    numberOfUnassessedLeads?: number;
    postalCode?: string;
    price?: string;
    rooms?: number;
    status?: string;
    street?: string;
    surface?: number;
}

interface ListingRowProps {
    listing: ListingDetails;
}

const ListingRow = ({
    listing,
}: ListingRowProps) => {
    const navigate = useNavigate();
    const routePath = useRoutePath();

    const [isListingSelected, setIsListingSelected] = useState<boolean>(false);

    const renderDate = (date) => {
        const realDate = moment(date);

        if (!realDate.isValid()) {
            return '-';
        }

        return realDate.fromNow();
    }

    const openListingModal = (e) => {
        e.stopPropagation();

        if (listing.status === 'available') {
            ArchiveListingModal.open(listing.id, false, null, () => { /***/ }, false, false);

            return;
        }

        UnarchiveListingModal.open(listing.id, false, null, `${listing.street} ${listing.houseNumber}, ${listing.city}`, () => { /***/ });
    }

    const handleCheckboxClick = (e?: MouseEvent<HTMLInputElement>) => {
        e?.stopPropagation();

        setIsListingSelected(!isListingSelected);

        if (isListingSelected) {
            BulkActionDropdownNew.remove(listing.id);

            return;
        }

        BulkActionDropdownNew.store(listing.id);
    }

    const navigateToCandidates = (e) => {
        e.stopPropagation();

        navigate(routePath('ListingDetails', {
            id: listing.id
        }, {
            status: 'none',
        }));
    }

    const navigateToApproved = (e) => {
        e.stopPropagation();

        navigate(routePath('ListingDetails', {
            id: listing.id
        }, {
            status: 'suitable',
        }));
    }

    const navigateToAssessment = (e) => {
        e.stopPropagation();

        navigate(routePath('ListingDetails', {
            id: listing.id
        }, {
            status: 'assessment',
        }));
    }

    const handleBulkActionEvent = (e: BulkActionDropdownCustomEventType) => {
        if (e.detail == null) {
            return;
        }

        const { shouldSelectAll } = e.detail;
        setIsListingSelected(shouldSelectAll);

        if (shouldSelectAll) {
            BulkActionDropdownNew.store(listing.id);
        } else {
            BulkActionDropdownNew.remove(listing.id);
        }
    };


    useEffect(() => {
        BulkActionDropdownNew.addListener(handleBulkActionEvent);

        return () => {
            BulkActionDropdownNew.removeListener(handleBulkActionEvent);
            BulkActionDropdownNew.setShouldSelectAll(false);
            BulkActionDropdownNew.remove(listing.id);
        }
    }, [])

    if (!listing) {
        return null;
    }

    return (
        <tr className={classNames(styles.listingRow, isListingSelected && styles.selected)} onClick={navigateToCandidates}>
            <td>
                <CheckboxInput
                    name='checkbox'
                    checked={isListingSelected}
                    onClick={handleCheckboxClick}
                />
            </td>
            <td className={styles.listingPropertiesContainer}>
                <div className={styles.listingDetails}>
                    <Text
                        size={FontSizeEnum.Lg}
                        weight={FontWeightEnum.Bold}
                        color={ColorEnum.Primary700}
                        className={styles.nowrap}
                    >
                        {`${listing.street} ${listing.houseNumber}, ${listing.city}`}
                    </Text>
                    <div className={styles.listingProperties}>
                        <IconWithText
                            className={styles.listingInfo}
                            icon={<MoneyEuroCircleLineIcon />}
                            fontSize={FontSizeEnum.Sm}
                        >
                            {formatPrice(Number(listing.price), false)}
                        </IconWithText>
                        <IconWithText
                            className={styles.listingInfo}
                            icon={<Ruler2LineIcon />}
                            fontSize={FontSizeEnum.Sm}
                        >
                            {formatSurface(Number(listing.surface))}
                        </IconWithText>
                        <IconWithText
                            className={styles.listingInfo}
                            icon={<DoorOpenLineIcon />}
                            fontSize={FontSizeEnum.Sm}
                        >
                            {formatRooms(Number(listing.rooms))}
                        </IconWithText>
                    </div>
                </div>
            </td>
            <td>
                <Text
                    className={styles.nowrap}
                    color={ColorEnum.Gray900}
                    size={FontSizeEnum.Sm}
                >
                    {renderDate(listing.createdAt)}
                </Text>
            </td>
            <td>
                <Text
                    className={styles.nowrap}
                    color={ColorEnum.Gray900}
                    size={FontSizeEnum.Sm}
                >
                    {renderDate(listing.availableAt)}
                </Text>
            </td>
            <td>
                <ButtonNew variant='secondary-gray' className={styles.buttonContainer} onClick={navigateToCandidates}>
                    <UserAddLineIcon /> {listing.numberOfUnassessedLeads > 999 ? '999+' : listing.numberOfUnassessedLeads}
                </ButtonNew>
            </td>
            <td>
                <ButtonNew variant='secondary-gray' className={styles.buttonContainer} onClick={navigateToAssessment}>
                    <UserSearchLineIcon /> {listing.numberOfLeadsToAssess > 999 ? '999+' : listing.numberOfLeadsToAssess}
                </ButtonNew>
            </td>
            <td>
                <ButtonNew variant='secondary-gray' className={styles.buttonContainer} onClick={navigateToApproved}>
                    <UserFollowLineIcon /> {listing.numberOfSuitableLeads > 999 ? '999+' : listing.numberOfSuitableLeads}
                </ButtonNew>
            </td>
            <td>
                <ArchiveButton status={listing.status} onClick={openListingModal} />
            </td>
        </tr>
    )
}

export default ListingRow;
